// Fix: Replaced Playlist icon with ViewList to resolve import conflicts
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Fab,
  Alert,
  AlertTitle,
  CircularProgress,
  Skeleton,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Stop,
  Add,
  Delete,
  Edit,
  Schedule as ScheduleIcon,
  Event,
  Monitor,
  ViewList as PlaylistIcon,
  Person,
  CalendarToday,
  AccessTime,
  Info,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';

import {
  EventStatus,
  Priority,
  ScheduleStatus,
  ScheduleType
} from '../../types/index';
import type {
  Schedule,
  ScheduleWithEvents,
  ScheduleEvent,
  ScheduleEventWithDetails
} from '../../types/index';
import apiClient from '../../services/api';
import ScheduleEventDialog from './ScheduleEventDialog';
import { invalidateScheduleData } from '../../utils/cacheInvalidation';

interface ScheduleDetailsDialogProps {
  open: boolean;
  schedule: Schedule | null;
  onClose: () => void;
  onUpdate?: (schedule: Schedule) => void;
  onEdit?: (schedule: Schedule) => void;
  onEventEdit?: (event: ScheduleEvent) => void;
}

const EventStatusIcon: React.FC<{ status: EventStatus }> = ({ status }) => {
  switch (status) {
    case 'active':
      return <PlayArrow color="success" fontSize="small" />;
    case 'scheduled':
      return <ScheduleIcon color="warning" fontSize="small" />;
    case 'completed':
      return <CheckCircle color="success" fontSize="small" />;
    case 'cancelled':
      return <ErrorIcon color="error" fontSize="small" />;
    default:
      return <Event color="action" fontSize="small" />;
  }
};

const PriorityChip: React.FC<{ priority: Priority }> = ({ priority }) => {
  const { t } = useTranslation();

  const getPriorityColor = (p: Priority): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (p) {
      case 20: return 'error';    // Critical
      case 15: return 'warning';  // High
      case 10: return 'primary';  // Normal
      case 5: return 'info';      // Low
      case 1: return 'default';   // Very Low
      default: return 'default';
    }
  };

  const getPriorityLabel = (p: Priority): string => {
    switch (p) {
      case 20: return t('common.priority.critical');
      case 15: return t('common.priority.high');
      case 10: return t('common.priority.normal');
      case 5: return t('common.priority.low');
      case 1: return t('common.priority.veryLow');
      default: return t('common.priority.normal');
    }
  };

  return (
    <Chip
      label={getPriorityLabel(priority)}
      color={getPriorityColor(priority)}
      size="small"
      variant="outlined"
    />
  );
};

const ScheduleTypeChip: React.FC<{ scheduleType: ScheduleType }> = ({ scheduleType }) => {
  const { t } = useTranslation();

  const getTypeColor = (type: ScheduleType): "default" | "primary" | "secondary" | "info" => {
    switch (type) {
      case 'once': return 'primary';
      case 'daily': return 'info';
      case 'weekly': return 'secondary';
      case 'monthly': return 'default';
      case 'custom': return 'info';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={t(`schedule.type.${scheduleType}`)}
      color={getTypeColor(scheduleType)}
      size="small"
      variant="outlined"
    />
  );
};

const ScheduleDetailsDialog: React.FC<ScheduleDetailsDialogProps> = ({
  open,
  schedule,
  onClose,
  onUpdate,
  onEdit,
  onEventEdit,
}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null);

  const dateLocale = i18n.language === 'ru' ? ru : enUS;

  // Получаем полную информацию о расписании с событиями
  const { data: scheduleWithEvents, isLoading, error, refetch } = useQuery({
    queryKey: ['scheduleWithEvents', schedule?.id],
    queryFn: () => schedule ? apiClient.getScheduleWithEvents(schedule.id) : null,
    enabled: !!schedule?.id && open,
  });

  // Мутация для обновления статуса события
  const updateEventStatusMutation = useMutation({
    mutationFn: ({ eventId, status }: { eventId: number; status: EventStatus }) =>
      apiClient.updateScheduleEventStatus(eventId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleWithEvents', schedule?.id] });
      invalidateScheduleData(queryClient);
    },
  });

  // Мутация для удаления события
  const deleteEventMutation = useMutation({
    mutationFn: (eventId: number) => apiClient.deleteScheduleEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleWithEvents', schedule?.id] });
      invalidateScheduleData(queryClient);
    },
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleUpdateEventStatus = (eventId: number, status: EventStatus) => {
    updateEventStatusMutation.mutate({ eventId, status });
  };

  const handleDeleteEvent = (eventId: number) => {
    if (window.confirm(t('schedule.events.confirmDelete'))) {
      deleteEventMutation.mutate(eventId);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventDialogOpen(true);
  };

  const handleEditEvent = (event: ScheduleEvent) => {
    setEditingEvent(event);
    setEventDialogOpen(true);
  };

  const handleEventSaved = (event: ScheduleEvent) => {
    // Обновляем кеш
    queryClient.invalidateQueries({ queryKey: ['scheduleWithEvents', schedule?.id] });
    invalidateScheduleData(queryClient);

    // Закрываем диалог
    setEventDialogOpen(false);
    setEditingEvent(null);
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}${t('common.time.hours')} ${mins}${t('common.time.minutes')}`;
    }
    return `${mins}${t('common.time.minutes')}`;
  };

  if (!schedule) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" component="div">
              {schedule.name}
            </Typography>
            <ScheduleTypeChip scheduleType={schedule.schedule_type} />
            {schedule.is_active ? (
              <Chip label={t('common.active')} color="success" size="small" />
            ) : (
              <Chip label={t('common.inactive')} color="default" size="small" />
            )}
          </Box>
          <Box>
            <Tooltip title={t('common.refresh')}>
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={80} />
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error">
            <AlertTitle>{t('common.error')}</AlertTitle>
            {t('common.loadError')}
          </Alert>
        ) : (
          <Stack spacing={3}>
            {/* Основная информация о расписании */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schedule.basicInfo')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('schedule.description')}:
                      </Typography>
                      <Typography variant="body1">
                        {schedule.description || t('common.noDescription')}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('schedule.author')}:
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Person fontSize="small" />
                          <Typography variant="body1">
                            {schedule.author?.full_name || t('common.unknown')}
                          </Typography>
                        </Stack>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('schedule.dateCreated')}:
                        </Typography>
                        <Typography variant="body1">
                          {format(new Date(schedule.created_at), 'dd.MM.yyyy HH:mm', { locale: dateLocale })}
                        </Typography>
                      </Box>

                      {schedule.updated_at !== schedule.created_at && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('schedule.dateUpdated')}:
                          </Typography>
                          <Typography variant="body1">
                            {format(new Date(schedule.updated_at), 'dd.MM.yyyy HH:mm', { locale: dateLocale })}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Статистика */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('schedule.statistics')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {scheduleWithEvents?.events_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schedule.events.total')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {scheduleWithEvents?.active_events_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schedule.events.active')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {scheduleWithEvents?.pending_events_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schedule.events.pending')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="text.secondary">
                        {scheduleWithEvents?.completed_events_count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('schedule.events.completed')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* События расписания */}
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">
                    {t('schedule.events.title')}
                  </Typography>
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    size="small"
                    onClick={handleCreateEvent}
                  >
                    {t('schedule.events.add')}
                  </Button>
                </Box>

                {!scheduleWithEvents?.events?.length ? (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('schedule.events.empty')}
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {scheduleWithEvents.events.map((event, index) => (
                      <React.Fragment key={event.id}>
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Avatar>
                              <EventStatusIcon status={event.status} />
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1">
                                  {event.name || event.title || t('schedule.events.untitled')}
                                </Typography>
                                <PriorityChip priority={event.priority} />
                              </Stack>
                            }
                            secondary={
                              <Stack spacing={1} mt={1}>
                                {/* Время */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <AccessTime fontSize="small" />
                                  <Typography variant="body2">
                                    {format(new Date(event.start_time), 'dd.MM.yyyy HH:mm', { locale: dateLocale })} -
                                    {format(new Date(event.end_time), 'HH:mm', { locale: dateLocale })}
                                  </Typography>
                                </Stack>

                                {/* Устройство */}
                                {event.device && (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Monitor fontSize="small" />
                                    <Typography variant="body2">
                                      {event.device.name}
                                    </Typography>
                                  </Stack>
                                )}

                                {/* Плейлист */}
                                {event.playlist && (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <PlaylistIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {event.playlist.name}
                                    </Typography>
                                  </Stack>
                                )}

                                {/* Описание */}
                                {event.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {event.description}
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />

                          <ListItemSecondaryAction>
                            <Stack direction="row" spacing={1}>
                              {event.status === 'scheduled' && (
                                <Tooltip title={t('schedule.events.start')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateEventStatus(event.id, 'active')}
                                    disabled={updateEventStatusMutation.isPending}
                                  >
                                    <PlayArrow />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {event.status === 'active' && (
                                <Tooltip title={t('schedule.events.stop')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateEventStatus(event.id, 'completed')}
                                    disabled={updateEventStatusMutation.isPending}
                                  >
                                    <Stop />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Tooltip title={t('common.edit')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditEvent(event)}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={t('common.delete')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteEvent(event.id)}
                                  disabled={deleteEventMutation.isPending}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </ListItemSecondaryAction>
                        </ListItem>

                        {index < scheduleWithEvents.events.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
        {onEdit && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => onEdit(schedule)}
          >
            {t('common.edit')}
          </Button>
        )}
      </DialogActions>

      {/* Диалог событий */}
      <ScheduleEventDialog
        open={eventDialogOpen}
        onClose={() => {
          setEventDialogOpen(false);
          setEditingEvent(null);
        }}
        onSave={handleEventSaved}
        event={editingEvent}
        scheduleId={schedule?.id}
        mode={editingEvent ? 'edit' : 'create'}
      />
    </Dialog>
  );
};

export default ScheduleDetailsDialog;
